/**
 * Date-only values (a voucher's validity, a product's expiry date) are days,
 * not instants. The API sends them as 'YYYY-MM-DD' or as UTC midnight
 * ('YYYY-MM-DDT00:00:00.000Z'); read as an instant, UTC midnight is the
 * previous evening west of Greenwich, which is how a date input ended up one
 * day early. These helpers keep them as days end to end.
 */
/**
 * Time zone the business counts its days in, the same as the backend
 * (lib/util/voucherValidity.ts) and the default of components/DateTime.
 * @const BUSINESS_TIME_ZONE
 * @since 1.0.0
 */
export const BUSINESS_TIME_ZONE = 'America/Montreal';
/**
 * 'YYYY-MM-DD' of a date-only value, or null.
 * @function toDay
 * @since 1.0.0
 */
export function toDay(value) {
    if (!value)
        return null;
    if (typeof value === 'string') {
        const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : null;
    }
    // A Date standing for a date-only value is UTC midnight
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${value.getUTCFullYear()}-${month}-${day}`;
}
/**
 * Local midnight of a date-only value, for display or a date picker.
 * @function dayToLocalDate
 * @since 1.0.0
 */
export function dayToLocalDate(value) {
    const day = toDay(value);
    if (!day)
        return null;
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y, m - 1, d);
}
/**
 * Value to give a FormDate for a date-only value: local midnight of that day,
 * so the picker shows the day itself.
 * @function dayToInputValue
 * @since 1.0.0
 */
export function dayToInputValue(value) {
    var _a, _b;
    return (_b = (_a = dayToLocalDate(value)) === null || _a === void 0 ? void 0 : _a.toISOString()) !== null && _b !== void 0 ? _b : null;
}
/**
 * Day picked in a FormDate, as 'YYYY-MM-DD'. FormDate hands back an ISO
 * instant of the picked day in local time; splitting it on 'T' gives the UTC
 * day instead, which is the next one in the evening.
 * @function inputValueToDay
 * @since 1.0.0
 */
export function inputValueToDay(value) {
    if (!value)
        return null;
    const date = new Date(value);
    if (isNaN(date.getTime()))
        return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}
/**
 * Today in the business time zone, as 'YYYY-MM-DD'.
 * @function today
 * @since 1.0.0
 */
export function today(now = new Date()) {
    // en-CA formats as YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: BUSINESS_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(now);
}
/**
 * A day plus a number of days, as 'YYYY-MM-DD'.
 * @function addDays
 * @since 1.0.0
 */
export function addDays(day, days) {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/**
 * Days from today to a date-only value; 0 on that day, negative after.
 * @function daysUntil
 * @since 1.0.0
 */
export function daysUntil(value, now = new Date()) {
    const day = toDay(value);
    if (!day)
        return null;
    return Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today(now)}T00:00:00Z`)) /
        86400000);
}
